import { useEffect, useState } from 'react';
import { getDisplayDetailsForValue } from '../utils/reports';
import { calculateReportScores } from '../utils/reports';
import useSnackBarAlertMessages from '../hooks/useSnackBarAlertMessages';
import useBackendApi from '../hooks/useBackendApi';
import { CompoundDataWithScores, FullCompoundMetaData, ReportDetailsAPI } from '../types/reports';

const backendURL = process.env.REACT_APP_BACKEND_URL;

const useReportAndCompoundMeta = (initialState: any) => {
  const [reportsData, setReportsData] = useState<ReportDetailsAPI[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [fullCompoundMetaData, setFullCompoundMetaData] = useState<FullCompoundMetaData>({});
  const [reportsDataWithScores, setReportsDataWithScores] = useState<CompoundDataWithScores[]>([]);
  const helpId = 'pfas_table_components_info';
  const { addAlertMessages } = useSnackBarAlertMessages();
  const { apiFetchRequest } = useBackendApi();

  const sortReportsByDate = (reports: CompoundDataWithScores[]): CompoundDataWithScores[] => {
    return reports.sort((a, b) => new Date(a.report_date).getTime() - new Date(b.report_date).getTime());
  };

  useEffect(() => {
    const fetchReportsAndCompoundMeta = async () => {
      try {
        const resultsResponse = await apiFetchRequest({ endpoint: `${backendURL}/reports/` });

        if (resultsResponse.status !== 200) {
          const { message } = resultsResponse.body;
          addAlertMessages({ message: 'Error fetching details', severityType: 'error' });
          if (message.toLowerCase().includes('report data not found')) {
            setErrorMessage('No Reports Found');
          } else {
            setErrorMessage('Something unexpected happened while retrieving your reports...');
          }
          return;
        }

        const { reports } = resultsResponse.body;
        if (reports !== undefined && reports.length > 0) {
          setReportsData(reports);
        } else {
          setErrorMessage('There are no reports to display at the moment.');
        }
      } catch (error) {
        console.log('error fetching reports', error);
        setErrorMessage('Something unexpected happened while retrieving your report...');
      }

      const compoundMetaResponse = await apiFetchRequest({ endpoint: `${backendURL}/help/${helpId}` });
      if (compoundMetaResponse.status !== 200) {
        addAlertMessages({ message: 'Error fetching details', severityType: 'error' });
        return;
      }
      const jsonString = compoundMetaResponse.body.h.replace(/'/g, '"');
      const compoundMetaData = JSON.parse(jsonString.replace(/False/g, 'false').replace(/True/g, 'true'));
      setFullCompoundMetaData(compoundMetaData);
      setLoading(false);

      if (initialState) {
        setReportsDataWithScores(initialState.reportsDataWithScores);
        return;
      }
    };
    fetchReportsAndCompoundMeta();
  }, []);

  useEffect(() => {
    if (initialState) return;

    if (reportsData.length > 0 && Object.keys(fullCompoundMetaData).length > 0 && reportsDataWithScores.length === 0) {
      const calculatedResultsScores: CompoundDataWithScores[] = reportsData.map((report: ReportDetailsAPI) => {
        const compoundDataWithDdfv = report.report_format_and_values.map((item) => {
          const ddfv = getDisplayDetailsForValue(item.value, item.reportFormat, fullCompoundMetaData, report.test_id);
          return { ddfv, value: item.value, reportFormat: item.reportFormat };
        });

        let compoundMetaData = fullCompoundMetaData[report.test_id];
        const { totalCompoundsDetected, totalPFASScore, nasemScore, nasemLevel } = calculateReportScores(
          compoundDataWithDdfv,
          compoundMetaData,
        );

        return {
          ...report,
          compoundDataWithDdfv,
          totalCompoundsDetected,
          totalPFASScore,
          nasemScore,
          nasemLevel,
        };
      });

      setReportsDataWithScores(sortReportsByDate(calculatedResultsScores));
    }
  }, [loading, reportsData, fullCompoundMetaData, reportsDataWithScores]);

  return {
    fullCompoundMetaData,
    reportsDataWithScores,
    loading,
    errorMessage,
  };
};

export default useReportAndCompoundMeta;
